import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "street", label: "Street Apparel", testid: "waitlist-interest-street" },
  { value: "couture", label: "Couture", testid: "waitlist-interest-couture" },
  { value: "both", label: "Both", testid: "waitlist-interest-both" },
];

const inputCls =
  "w-full border border-[#221812]/20 bg-[#FCFAF4] px-4 py-3.5 text-base text-[#221812] placeholder-[#A39A8D] outline-none transition-colors duration-300 focus:border-[#9A7B3F] focus:ring-1 focus:ring-[#9A7B3F]";

const labelCls = "mb-2 block text-xs uppercase tracking-[0.25em] text-[#55504A]";

export default function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", desired_item: "", website: "" });
  const [interests, setInterests] = useState([]);
  const [promo, setPromo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const toggleInterest = (v) =>
    setInterests((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { ...form, interests, consent_promo_email: promo });
      setDone({ ...form, interests, promo });
      toast.success("You're on the SANZONE April 2027 waitlist.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="relative border-t border-[#221812]/10 px-6 py-28 sm:px-12 sm:py-36 lg:px-20" data-testid="waitlist-section">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Eyebrow>[ 04 — Free Waitlist ]</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 font-display text-3xl tracking-tight text-[#221812] sm:text-5xl" data-testid="waitlist-heading">
            First access. <span className="italic text-[#5E1A22]">April 2027.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-sm leading-relaxed text-[#55504A] sm:text-base">
            Free to join. No purchase required. Joining does not reserve a product.
          </p>
        </Reveal>

        <Reveal delay={0.25} className="mt-12">
          {done ? (
            <div className="border border-[#9A7B3F]/50 bg-[#F9F5EB] p-8 sm:p-10" data-testid="waitlist-success">
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#5E1A22]/50">
                  <Check className="h-5 w-5 text-[#5E1A22]" />
                </span>
                <h3 className="font-display text-xl text-[#221812] sm:text-2xl">Welcome to the pack, {done.name}.</h3>
              </div>
              <dl className="mt-8 space-y-3 text-sm text-[#55504A]">
                <div className="flex gap-3"><dt className="w-32 shrink-0 text-[#8C857B]">Email</dt><dd className="text-[#221812]">{done.email}</dd></div>
                {done.phone && <div className="flex gap-3"><dt className="w-32 shrink-0 text-[#8C857B]">Phone</dt><dd>{done.phone}</dd></div>}
                <div className="flex gap-3"><dt className="w-32 shrink-0 text-[#8C857B]">Interest</dt><dd>{done.interests.length ? done.interests.map((i) => INTERESTS.find((x) => x.value === i)?.label).join(", ") : "Not specified"}</dd></div>
                {done.desired_item && <div className="flex gap-3"><dt className="w-32 shrink-0 text-[#8C857B]">Desired item</dt><dd>{done.desired_item}</dd></div>}
                <div className="flex gap-3"><dt className="w-32 shrink-0 text-[#8C857B]">Promo emails</dt><dd>{done.promo ? "Opted in" : "Not opted in"}</dd></div>
              </dl>
              <p className="mt-8 border-t border-[#221812]/10 pt-6 text-xs leading-relaxed text-[#8C857B]">
                Your details are saved. We'll reach out with launch news ahead of April 2027.
              </p>
              <button
                onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", desired_item: "", website: "" }); setInterests([]); setPromo(false); }}
                className="mt-6 text-xs uppercase tracking-[0.3em] text-[#5E1A22] underline decoration-[#5E1A22]/40 underline-offset-8 transition-colors duration-300 hover:text-[#221812]"
                data-testid="waitlist-add-another-btn"
              >
                Add another response
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form">
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="wl-website">Leave this field empty</label>
                <input id="wl-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="wl-name" className={labelCls}>Name *</label>
                  <input id="wl-name" type="text" required maxLength={120} value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="waitlist-name-input" />
                </div>
                <div>
                  <label htmlFor="wl-email" className={labelCls}>Email *</label>
                  <input id="wl-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="waitlist-email-input" />
                </div>
                <div>
                  <label htmlFor="wl-phone" className={labelCls}>Phone <span className="text-[#8C857B]">(optional)</span></label>
                  <input id="wl-phone" type="tel" maxLength={40} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Not required" data-testid="waitlist-phone-input" />
                </div>
                <div>
                  <label htmlFor="wl-item" className={labelCls}>Desired item <span className="text-[#8C857B]">(optional)</span></label>
                  <input id="wl-item" type="text" maxLength={200} value={form.desired_item} onChange={set("desired_item")} className={inputCls} placeholder="e.g. Hoodie" data-testid="waitlist-desired-item-input" />
                </div>
              </div>

              <fieldset className="mt-8">
                <legend className="mb-3 text-xs uppercase tracking-[0.25em] text-[#55504A]">I'm interested in <span className="text-[#8C857B]">(optional)</span></legend>
                <div className="flex flex-wrap gap-3">
                  {INTERESTS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`cursor-pointer border px-4 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                        interests.includes(opt.value)
                          ? "border-[#5E1A22] bg-[#5E1A22]/5 text-[#5E1A22]"
                          : "border-[#221812]/20 text-[#55504A] hover:border-[#221812]/50"
                      }`}
                    >
                      <input type="checkbox" className="sr-only" checked={interests.includes(opt.value)} onChange={() => toggleInterest(opt.value)} data-testid={opt.testid} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="mt-8 flex cursor-pointer items-start gap-3 text-sm text-[#55504A]">
                <input
                  type="checkbox"
                  checked={promo}
                  onChange={(e) => setPromo(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#5E1A22]"
                  data-testid="waitlist-promo-optin"
                />
                <span>I'd also like occasional SANZONE promotional emails <span className="text-[#8C857B]">(optional, unchecked by default)</span></span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="mt-10 w-full bg-[#221812] px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#F4EEE3] transition-colors duration-300 hover:bg-[#5E1A22] disabled:opacity-50 sm:w-auto"
                data-testid="waitlist-submit-btn"
              >
                {submitting ? "Joining…" : "Join the April 2027 Waitlist"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
